import React, { FC, MouseEvent, useCallback, useState } from "react";
import { Box } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import Popover from "@mui/material/Popover";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import LabelIcon from "@mui/icons-material/Label";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import styled from "styled-components";
import {
  FoodTagBasic,
  MenuItemFoodTagOverride,
} from "client/jspPlatformExperiment";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import EffectiveTagsPreview from "components/menu/ui/tagOverride/EffectiveTagsPreview";
import useFoodEffectiveTags from "hooks/food/useFoodEffectiveTags";
import {
  describeOverrides,
  isMolleOnlyOverride,
  setMolleOnly,
  TagOverrides,
} from "models/menu/menuItemTagOverride";

/**
 * Pill shown left of the button while an override is active. Warning amber,
 * so it flags a non-default setting and does not blend into the blue button.
 * Clicking it opens the effective-tags preview.
 */
const OverrideIndicator = styled(ButtonBase)`
  white-space: nowrap;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.6;
  padding: 0 0.6rem;
  color: white;
  background-color: rgb(245, 166, 35);
  border-radius: 1rem;
`;

export interface MenuItemTagOverrideButtonProps {
  foodId: number;
  /** The food's Molle texture tag; null when the food does not carry one. */
  molleTag: FoodTagBasic | null;
  /** Site the effective-tags preview is evaluated for (navbar site). */
  previewSiteId: number | null;
  overrides: TagOverrides;
  onChange: (overrides: MenuItemFoodTagOverride[]) => void;
}

const NO_OVERRIDES: MenuItemFoodTagOverride[] = [];

const MOLLE_ONLY_HELP =
  "Keep only the Molle texture tag on this menu; the food's other texture " +
  "tags are hidden. Allergens, diet type and liquid thickness are untouched.";

/**
 * Top-right control of a main menu item: an indicator pill plus a button that
 * opens a two-entry menu, "No override" or "Molle only" (spec §4). The
 * button is disabled without a food; the "Molle only" entry is disabled when
 * the food has no Molle texture tag.
 */
const MenuItemTagOverrideButton: FC<MenuItemTagOverrideButtonProps> = ({
  foodId,
  molleTag,
  previewSiteId,
  overrides,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [previewAnchorEl, setPreviewAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const previewOpen = Boolean(previewAnchorEl);
  const rows = overrides ?? NO_OVERRIDES;
  const label = describeOverrides(rows);
  const molleOnlyOn = rows.some(isMolleOnlyOverride);

  // The preview endpoint can be slow, so it only runs while the popover is
  // open, never on ordinary edits.
  const preview = useFoodEffectiveTags({
    foodId,
    siteId: previewSiteId,
    overrides: rows,
    enabled: previewOpen,
  });

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handlePreviewOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setPreviewAnchorEl(event.currentTarget);
    },
    [],
  );
  const handlePreviewClose = useCallback(() => setPreviewAnchorEl(null), []);

  const choose = (enabled: boolean): void => {
    handleClose();
    if (enabled === molleOnlyOn && (enabled || rows.length === 0)) {
      return;
    }
    if (!enabled) {
      onChange([]);
      return;
    }
    if (molleTag) {
      onChange(setMolleOnly({ molleTag, enabled: true }));
    }
  };

  let tooltip = "Menu-level tag override";
  if (foodId <= 0) {
    tooltip = "Select a food first";
  } else if (label) {
    tooltip = `Menu-level tag override (${label})`;
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {label ? (
        <Tooltip title="Preview the tags this item reports">
          <OverrideIndicator
            onClick={handlePreviewOpen}
            aria-label={`Preview effective tags (${label})`}
            aria-haspopup="dialog"
            aria-expanded={previewOpen ? "true" : undefined}
          >
            {label}
          </OverrideIndicator>
        </Tooltip>
      ) : null}
      <Popover
        open={previewOpen}
        anchorEl={previewAnchorEl}
        onClose={handlePreviewClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <EffectiveTagsPreview preview={preview} />
      </Popover>
      <Tooltip title={tooltip}>
        {/* The span keeps the tooltip working while the button is disabled. */}
        <span>
          <SoftSquareIconButton
            positive={!!label}
            disabled={foodId <= 0}
            aria-label={tooltip}
            aria-haspopup="menu"
            aria-expanded={open ? "true" : undefined}
            onClick={handleOpen}
          >
            {label ? <LabelIcon /> : <LabelOutlinedIcon />}
          </SoftSquareIconButton>
        </span>
      </Tooltip>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem selected={rows.length === 0} onClick={() => choose(false)}>
          No override
        </MenuItem>
        <Tooltip
          title={
            molleTag ? MOLLE_ONLY_HELP : "This food has no Molle texture tag."
          }
          placement="left"
        >
          {/* The span keeps the tooltip working while the entry is disabled. */}
          <span>
            <MenuItem
              selected={molleOnlyOn}
              disabled={!molleTag}
              onClick={() => choose(true)}
            >
              Molle only
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </Box>
  );
};

export default MenuItemTagOverrideButton;
